'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import ReactSelect, { components } from 'react-select';
import ReactCreatableSelect from 'react-select/creatable';
import { ChevronDown, X } from 'lucide-react';
import { cn } from '../utils/cn';
// Once a multi-select's chips wrap past `maxRows`, hides the overflow chips
// and shows a single "+N more" pill instead — measured from the actual
// rendered chip positions (offsetTop) so it adapts to however wide each
// chip's label happens to be, not a fixed chip-count guess.
function createRowLimitedValueContainer(maxRows) {
    return function RowLimitedValueContainer(props) {
        const wrapRef = React.useRef(null);
        const [hiddenFrom, setHiddenFrom] = React.useState(null);
        const total = props.getValue().length;
        React.useLayoutEffect(() => {
            const wrap = wrapRef.current;
            if (!wrap || total === 0) {
                setHiddenFrom(null);
                return;
            }
            const chips = Array.from(wrap.querySelectorAll('.erp-select__multi-value'));
            chips.forEach(el => { el.style.display = ''; });
            if (chips.length === 0) {
                setHiddenFrom(null);
                return;
            }
            let rowTop = null;
            let rowCount = 0;
            let cutoff = null;
            chips.forEach((el, i) => {
                if (rowTop === null || Math.abs(el.offsetTop - rowTop) > 2) {
                    rowCount++;
                    rowTop = el.offsetTop;
                }
                if (rowCount > maxRows && cutoff === null) {
                    cutoff = i;
                }
            });
            chips.forEach((el, i) => {
                if (cutoff !== null && i >= cutoff)
                    el.style.display = 'none';
            });
            setHiddenFrom(cutoff);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [total, JSON.stringify(props.getValue().map((v) => v.value))]);
        return (_jsxs(components.ValueContainer, { ...props, children: [_jsx("div", { ref: wrapRef, style: { display: 'contents' }, children: props.children }), hiddenFrom !== null && (_jsxs("div", { className: "mx-1 my-0.5 rounded px-2 py-0.5 text-[13px] font-medium bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 whitespace-nowrap", children: ["+", total - hiddenFrom, " more"] }))] }));
    };
}
// Custom components for icons
const DropdownIndicator = (props) => (_jsx(components.DropdownIndicator, { ...props, children: _jsx(ChevronDown, { className: "h-4 w-4 text-gray-400 dark:text-gray-500" }) }));
const ClearIndicator = (props) => (_jsx(components.ClearIndicator, { ...props, children: _jsx(X, { className: "h-4 w-4 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300" }) }));
const MultiValueRemove = (props) => (_jsx(components.MultiValueRemove, { ...props, children: _jsx(X, { className: "h-3 w-3" }) }));
export function Select({ label, error, helperText, required, className, darkMode, onChange, isCreatable = false, maxVisibleRows, ...props }) {
    // Swap to the creatable variant only when explicitly requested — same styling/theme.
    const SelectComponent = isCreatable ? ReactCreatableSelect : ReactSelect;
    const RowLimitedValueContainer = React.useMemo(() => (maxVisibleRows ? createRowLimitedValueContainer(maxVisibleRows) : undefined), [maxVisibleRows]);
    // Auto-detect dark mode from document if not explicitly set
    const [isDarkMode, setIsDarkMode] = React.useState(darkMode ?? (typeof window !== 'undefined' && document.documentElement.classList.contains('dark')));
    // Listen for dark mode changes
    React.useEffect(() => {
        if (darkMode !== undefined) {
            setIsDarkMode(darkMode);
            return;
        }
        if (typeof window === 'undefined')
            return;
        const updateDarkMode = () => {
            setIsDarkMode(document.documentElement.classList.contains('dark'));
        };
        // Initial check
        updateDarkMode();
        // Watch for class changes on documentElement
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                    updateDarkMode();
                }
            });
        });
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['class']
        });
        return () => observer.disconnect();
    }, [darkMode]);
    // A multi-select whose chips wrap must grow with them instead of clipping the second row.
    // It stops at ~3 rows and scrolls inside the box after that. `maxVisibleRows` keeps the
    // fixed single-row box (it collapses the overflow into a "+N more" pill instead).
    const growsWithChips = !!props.isMulti && !maxVisibleRows;
    const customStyles = {
        control: (provided, state) => ({
            ...provided,
            backgroundColor: isDarkMode ? '#1f2937' : '#ffffff', // Match input field background
            borderColor: error
                ? '#ef4444'
                : state.isFocused
                    ? '#3b82f6'
                    : (isDarkMode ? '#4b5563' : '#d1d5db'),
            borderWidth: '1px',
            borderRadius: '6px',
            boxShadow: state.isFocused ? '0 0 0 2px #3b82f640' : 'none',
            cursor: 'pointer',
            '&:hover': {
                borderColor: error
                    ? '#ef4444'
                    : (isDarkMode ? '#6b7280' : '#9ca3af'),
            },
            minHeight: '40px',
            ...(growsWithChips
                ? { height: 'auto' }
                : { maxHeight: '40px', height: '40px' }),
            fontSize: '14px', // Match input text-sm
            color: 'inherit', // Inherit text color from parent
        }),
        valueContainer: (provided) => ({
            ...provided,
            ...(growsWithChips
                ? { padding: '2px 12px', maxHeight: '112px', overflowY: 'auto' }
                : { padding: '0 12px', height: '40px' }),
        }),
        input: (provided) => ({
            ...provided,
            color: 'inherit', // Inherit from theme
            margin: '0',
            padding: '0',
            fontSize: '14px',
        }),
        placeholder: (provided) => ({
            ...provided,
            color: isDarkMode ? '#6b7280' : '#9ca3af', // Match TextField: gray-400 light, gray-500 dark
            fontSize: '14px',
        }),
        singleValue: (provided) => ({
            ...provided,
            color: 'inherit', // Inherit from theme
            fontSize: '14px',
        }),
        menu: (provided) => ({
            ...provided,
            backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
            border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            zIndex: 100000,
        }),
        menuPortal: (provided) => ({
            ...provided,
            zIndex: 100000,
        }),
        menuList: (provided) => ({
            ...provided,
            padding: '4px',
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected
                ? (isDarkMode ? '#3b82f6' : '#3b82f6')
                : state.isFocused
                    ? (isDarkMode ? '#374151' : '#f3f4f6')
                    : 'transparent',
            color: state.isSelected
                ? '#ffffff'
                : (isDarkMode ? '#ffffff' : '#111827'),
            borderRadius: '4px',
            margin: '2px 0',
            padding: '8px 12px',
            cursor: 'pointer',
            '&:hover': {
                backgroundColor: state.isSelected
                    ? (isDarkMode ? '#3b82f6' : '#3b82f6')
                    : (isDarkMode ? '#374151' : '#f3f4f6'),
            },
        }),
        multiValue: (provided) => ({
            ...provided,
            backgroundColor: isDarkMode ? '#374151' : '#e5e7eb',
            borderRadius: '4px',
        }),
        multiValueLabel: (provided) => ({
            ...provided,
            color: isDarkMode ? '#ffffff' : '#111827',
            fontSize: '14px',
        }),
        multiValueRemove: (provided) => ({
            ...provided,
            color: isDarkMode ? '#9ca3af' : '#6b7280',
            '&:hover': {
                backgroundColor: isDarkMode ? '#ef4444' : '#ef4444',
                color: '#ffffff',
            },
            borderRadius: '0 4px 4px 0',
        }),
        indicatorsContainer: (provided) => ({
            ...provided,
            ...(growsWithChips ? {} : { height: '40px' }),
        }),
        indicatorSeparator: (provided) => ({
            ...provided,
            backgroundColor: isDarkMode ? '#4b5563' : '#d1d5db',
            marginTop: '8px',
            marginBottom: '8px',
        }),
        dropdownIndicator: (provided) => ({
            ...provided,
            color: isDarkMode ? '#9ca3af' : '#6b7280',
            padding: '0 8px',
            '&:hover': {
                color: isDarkMode ? '#ffffff' : '#111827',
            },
        }),
        clearIndicator: (provided) => ({
            ...provided,
            color: isDarkMode ? '#9ca3af' : '#6b7280',
            padding: '0 8px',
            '&:hover': {
                color: isDarkMode ? '#ffffff' : '#111827',
            },
        }),
        noOptionsMessage: (provided) => ({
            ...provided,
            color: isDarkMode ? '#9ca3af' : '#6b7280',
            padding: '12px',
        }),
        loadingMessage: (provided) => ({
            ...provided,
            color: isDarkMode ? '#9ca3af' : '#6b7280',
            padding: '12px',
        }),
    };
    const handleChange = (newValue) => {
        if (onChange) {
            onChange(newValue);
        }
    };
    return (_jsxs("div", { className: cn("space-y-1", className), children: [label && (_jsxs("label", { className: "block text-sm font-medium text-gray-700 dark:text-gray-200", children: [label, required && _jsx("span", { className: "text-red-500 ml-1", children: "*" })] })), _jsx(SelectComponent, { ...props, styles: (() => {
                    const callerStyles = (props.styles ?? {});
                    const defaults = customStyles;
                    const keys = Array.from(new Set([...Object.keys(defaults), ...Object.keys(callerStyles)]));
                    const merged = {};
                    for (const key of keys) {
                        const defFn = defaults[key];
                        const callFn = callerStyles[key];
                        merged[key] = (base, state) => {
                            let out = base;
                            if (defFn)
                                out = defFn(out, state);
                            if (callFn)
                                out = callFn(out, state);
                            return out;
                        };
                    }
                    return merged;
                })(), components: {
                    DropdownIndicator,
                    ClearIndicator,
                    MultiValueRemove,
                    ...(RowLimitedValueContainer ? { ValueContainer: RowLimitedValueContainer } : {}),
                    ...(props.components ?? {}),
                }, onChange: handleChange, menuPortalTarget: typeof window !== 'undefined' ? document.body : null, 
                // "fixed" (not "absolute") so the body-portaled menu doesn't extend the
                // page height / add empty scroll space when opened inside a modal.
                menuPosition: "fixed", menuPlacement: "auto", theme: (theme) => ({
                    ...theme,
                    colors: {
                        ...theme.colors,
                        primary: '#3b82f6',
                        primary75: '#60a5fa',
                        primary50: '#93c5fd',
                        primary25: '#dbeafe',
                        danger: '#ef4444',
                        dangerLight: '#fecaca',
                        neutral0: isDarkMode ? '#1f2937' : '#ffffff',
                        neutral5: isDarkMode ? '#374151' : '#f9fafb',
                        neutral10: isDarkMode ? '#4b5563' : '#f3f4f6',
                        neutral20: isDarkMode ? '#6b7280' : '#e5e7eb',
                        neutral30: isDarkMode ? '#9ca3af' : '#d1d5db',
                        neutral40: isDarkMode ? '#d1d5db' : '#9ca3af',
                        neutral50: isDarkMode ? '#e5e7eb' : '#6b7280',
                        neutral60: isDarkMode ? '#f3f4f6' : '#4b5563',
                        neutral70: isDarkMode ? '#f9fafb' : '#374151',
                        neutral80: isDarkMode ? '#ffffff' : '#1f2937',
                        neutral90: isDarkMode ? '#ffffff' : '#111827',
                    },
                }) }), error && (_jsx("p", { className: "text-sm text-red-600 dark:text-red-400", children: error })), helperText && !error && (_jsx("p", { className: "text-sm text-gray-500 dark:text-gray-400", children: helperText }))] }));
}
export { ReactCreatableSelect as CreatableSelect };
export default Select;
